/**
 * Suivi des tournées en cours, vu du magasin (23/10/2026).
 *
 * Demande de Philippe : « le suivi des tournées n'est pas facile dans le
 * dashboard — il serait utile de recentrer la carte automatiquement, et sur un
 * point précis ; page accessible sur iPhone ».
 *
 * La carte se recentrait toutes les 20 s sur la MOYENNE de tous les points
 * (livreurs et clients, arrêts déjà livrés compris), au zoom courant : ni tout
 * le reste à faire dans le cadre, ni moyen de suivre un livreur ou de viser un
 * arrêt — et elle sautait sous les yeux de qui la regardait.
 *
 * Règles pures : réponse de l'API → tournées suivies ; ce qu'il reste à faire ;
 * âge de la dernière position ; et surtout le CADRAGE selon la cible choisie.
 */

export type EtatArret = 'a_faire' | 'en_route' | 'livre' | 'echec';

export interface ArretSuivi {
  id: string;
  rang: number;
  orderNumber: string;
  client: string;
  adresse: string;
  lat: number | null;
  lng: number | null;
  etat: EtatArret;
  /** Heure estimée de passage (ISO), si l'ordre a été calculé. */
  prevu: string | null;
  /** Heure réelle de la remise ou de l'échec (ISO). */
  fait: string | null;
}

export interface TourneeSuivi {
  id: string;
  livreur: string;
  statut: string;
  lat: number | null;
  lng: number | null;
  /** Dernière position transmise par le téléphone du livreur (ISO). */
  derniereMaj: string | null;
  arrets: ArretSuivi[];
}

export type Cible =
  | { type: 'tout' }
  | { type: 'tournee'; id: string }
  | { type: 'livreur'; id: string }
  | { type: 'arret'; tourneeId: string; arretId: string };

export type Cadrage =
  | { mode: 'cadre'; points: Array<[number, number]> }
  | { mode: 'centre'; point: [number, number]; zoom: number };

export const ZOOM_LIVREUR = 16;
export const ZOOM_ARRET = 17;
export const ZOOM_POINT_SEUL = 15;
export const ZOOM_DEPOT = 12;
/** Au-delà, la position affichée n'est plus celle du livreur : on le dit. */
export const POSITION_ANCIENNE_MS = 3 * 60_000;

/** Une couleur par tournée, dans l'ordre d'affichage (lisibles sur la carte OSM). */
export const COULEURS_TOURNEES = ['#2563eb', '#d97706', '#7c3aed', '#0d9488', '#db2777'];

const ETATS: Record<string, EtatArret> = {
  PENDING: 'a_faire',
  EN_ROUTE: 'en_route',
  DELIVERED: 'livre',
  FAILED: 'echec',
};

const nombre = (v: unknown): number | null => {
  const n = typeof v === 'string' ? parseFloat(v) : (v as number);
  return typeof n === 'number' && Number.isFinite(n) && n !== 0 ? n : null;
};

/** Seules les tournées EN ROUTE ont leur place sur la carte du suivi. */
export function versTourneesSuivi(data: any[]): TourneeSuivi[] {
  return (Array.isArray(data) ? data : [])
    .filter((d) => d?.status === 'STARTED' || d?.status === 'IN_PROGRESS')
    .map((d) => {
      const livreur =
        d.courier?.name ||
        (d.driver ? `${d.driver.first_name ?? ''} ${d.driver.last_name ?? ''}`.trim() || d.driver.name : null) ||
        'Livreur non désigné';
      const arrets: ArretSuivi[] = [...(d.orders || [])]
        .sort((a: any, b: any) => (a.position ?? 0) - (b.position ?? 0))
        .map((s: any, i: number) => {
          const a = s.order?.delivery_address || {};
          return {
            id: String(s.id),
            rang: i + 1,
            orderNumber: s.order?.order_number || '',
            client: s.order?.customer_name || 'Client',
            adresse: [a.street, [a.postal_code, a.city].filter(Boolean).join(' ')].filter(Boolean).join(', '),
            lat: nombre(a.latitude),
            lng: nombre(a.longitude),
            etat: ETATS[String(s.status || 'PENDING')] || 'a_faire',
            prevu: s.estimated_time || null,
            fait: s.actual_time || null,
          };
        });
      return {
        id: String(d.id),
        livreur,
        statut: String(d.status),
        lat: nombre(d.tracking?.current_lat),
        lng: nombre(d.tracking?.current_lng),
        derniereMaj: d.tracking?.last_update || null,
        arrets,
      };
    })
    .sort((a, b) => a.livreur.localeCompare(b.livreur, 'fr'));
}

export const arretsRestants = (t: TourneeSuivi): ArretSuivi[] =>
  t.arrets.filter((a) => a.etat === 'a_faire' || a.etat === 'en_route');

export const prochainArret = (t: TourneeSuivi): ArretSuivi | null => arretsRestants(t)[0] ?? null;

export const arretsFaits = (t: TourneeSuivi): number => t.arrets.filter((a) => a.etat === 'livre').length;

const point = (lat: number | null, lng: number | null): [number, number] | null =>
  lat !== null && lng !== null ? [lat, lng] : null;

export const positionLivreur = (t: TourneeSuivi) => point(t.lat, t.lng);
export const positionArret = (a: ArretSuivi) => point(a.lat, a.lng);

/** « à l'instant », « il y a 45 s », « il y a 3 min », « il y a 1 h 05 » — ancienne au-delà de 3 min. */
export function ageDePosition(iso: string | null, maintenant: Date): { texte: string; ancienne: boolean } | null {
  if (!iso) return null;
  const t = new Date(iso).getTime();
  if (!Number.isFinite(t)) return null;
  const ms = Math.max(0, maintenant.getTime() - t);
  const s = Math.round(ms / 1000);
  let texte: string;
  if (s < 10) texte = "à l'instant";
  else if (s < 60) texte = `il y a ${s} s`;
  else if (s < 3600) texte = `il y a ${Math.floor(s / 60)} min`;
  else texte = `il y a ${Math.floor(s / 3600)} h ${String(Math.floor((s % 3600) / 60)).padStart(2, '0')}`;
  return { texte, ancienne: ms > POSITION_ANCIENNE_MS };
}

/** Ce que la carte doit montrer d'une tournée : le livreur et ce qu'il lui reste. */
function pointsTournee(t: TourneeSuivi): Array<[number, number]> {
  const pts: Array<[number, number]> = [];
  const livreur = positionLivreur(t);
  if (livreur) pts.push(livreur);
  for (const a of arretsRestants(t)) {
    const p = positionArret(a);
    if (p) pts.push(p);
  }
  // Tout est livré : on garde la tournée entière dans le cadre plutôt que rien.
  if (pts.length === 0) {
    for (const a of t.arrets) {
      const p = positionArret(a);
      if (p) pts.push(p);
    }
  }
  return pts;
}

function depuisPoints(points: Array<[number, number]>, depot: [number, number] | null): Cadrage | null {
  if (points.length > 1) return { mode: 'cadre', points };
  if (points.length === 1) return { mode: 'centre', point: points[0], zoom: ZOOM_POINT_SEUL };
  return depot ? { mode: 'centre', point: depot, zoom: ZOOM_DEPOT } : null;
}

/**
 * Où regarder, selon la cible choisie. Une cible devenue introuvable (tournée
 * terminée, livreur sans position, arrêt sans coordonnées) retombe sur ce qui la
 * contient — jamais sur une carte vide.
 */
export function cadrage(tournees: TourneeSuivi[], cible: Cible, depot: [number, number] | null): Cadrage | null {
  if (cible.type === 'livreur') {
    const t = tournees.find((x) => x.id === cible.id);
    const p = t && positionLivreur(t);
    if (p) return { mode: 'centre', point: p, zoom: ZOOM_LIVREUR };
    return t ? depuisPoints(pointsTournee(t), depot) : cadrage(tournees, { type: 'tout' }, depot);
  }
  if (cible.type === 'arret') {
    const t = tournees.find((x) => x.id === cible.tourneeId);
    const a = t?.arrets.find((x) => x.id === cible.arretId);
    const p = a && positionArret(a);
    if (p) return { mode: 'centre', point: p, zoom: ZOOM_ARRET };
    return t ? depuisPoints(pointsTournee(t), depot) : cadrage(tournees, { type: 'tout' }, depot);
  }
  if (cible.type === 'tournee') {
    const t = tournees.find((x) => x.id === cible.id);
    if (t) return depuisPoints(pointsTournee(t), depot);
  }
  return depuisPoints(tournees.flatMap(pointsTournee), depot);
}

/** Libellé du bouton « Recentrer sur … ». */
export function libelleCible(tournees: TourneeSuivi[], cible: Cible): string {
  if (cible.type === 'livreur') {
    const t = tournees.find((x) => x.id === cible.id);
    return t ? t.livreur : 'le livreur';
  }
  if (cible.type === 'arret') {
    const a = tournees.find((x) => x.id === cible.tourneeId)?.arrets.find((x) => x.id === cible.arretId);
    return a ? `l'arrêt ${a.rang} — ${a.client}` : "l'arrêt";
  }
  if (cible.type === 'tournee') {
    const t = tournees.find((x) => x.id === cible.id);
    return t ? `la tournée de ${t.livreur}` : 'la tournée';
  }
  return 'toutes les tournées';
}
